import * as types from './types'
import { createReducer } from 'app/state/utils'
import { SignableDocument } from 'app/models/document-params'
import {
  FMDocumentMetadata,
  FMFacilityAmounts,
  FMLoanAmountsMapEntry,
  FMLoanDataWithStatus,
  FMSignatureBlock,
  FMSubTradeFee,
  FMTradedFacilityWithFees,
  FMWireInstructionsCalculation,
} from 'app/models/fm-metadata-params'
import { StableKey } from 'app/models/common-types'

export interface LockedFMMetadata {
  signableDocumentId: {
    _1: string
    _2: string
    _3: string
  }
  effectiveDate: string
  subTradeSystemId: string
  subTradeFees: FMSubTradeFee[]
  delayCompensationDate: string
  targetSettlementDate: string
  dealName: string
  dealId: StableKey
  paymentType: string
  tradeDate: string
  tradeType: string
  tradeAssociation: string
  tradeDocsType: string
  transferType: string
  interestTreatment: string
  assignmentFeePayType: string
  buyerId: string
  buyerName: string
  buyerMei: string
  buyerSigBlock: FMSignatureBlock | null
  sellerId: string
  sellerName: string
  sellerMei: string
  sellerSigBlock: FMSignatureBlock | null
  agentId: string
  agentName: string
  agentMei: string
  facilityAmounts: FMFacilityAmounts[]
  allLoans: FMLoanDataWithStatus[]
  tradedFacilityWithCalculatedFee: FMTradedFacilityWithFees[]
  loanAmountsMap: FMLoanAmountsMapEntry[]
  wireInstructionsCalculation: FMWireInstructionsCalculation
  createdAt: string
  observers: string[]
}

export interface FMMetadataEntry {
  data: FMDocumentMetadata | null
  isLoading: boolean
  error: string | null
}

export interface SignableDocumentState {
  signableDocuments: SignableDocument[]
  lockedFMMetadatas: LockedFMMetadata[]
  fmMetadataBySubTrade: Record<string, FMMetadataEntry>
  isLoading: boolean
  // True while a save is generating documents, so the document cells can spin
  // until the save completes (survives navigating between trade-entry tabs).
  isGenerating: boolean
  // Incremented on every successful document regeneration. Components that
  // fetch AA Document Metadata outside the store watch this to know to refetch.
  regeneratedCount: number
}

const initialState: SignableDocumentState = {
  signableDocuments: [],
  lockedFMMetadatas: [],
  fmMetadataBySubTrade: {},
  isLoading: false,
  isGenerating: false,
  regeneratedCount: 0,
}

const documentsReducer = createReducer(initialState)({
  [types.DOCUMENTS_REGENERATED]: (state: any) => {
    return {
      ...state,
      regeneratedCount: (state.regeneratedCount ?? 0) + 1,
    }
  },

  [types.GET_SIGNABLE_DOCUMENTS]: (state: any, payload: any) => {
    return {
      ...state,
      signableDocuments: payload.payload,
    }
  },

  [types.EDIT_SIGNABLE_DOCUMENT]: (state: any, payload: any) => {
    const index = state.signableDocuments.findIndex(
      (signableDocument: SignableDocument) =>
        signableDocument.contractId === payload.payload.data.contractId &&
        signableDocument.documentType === payload.payload.data.documentType,
    )
    if (index === -1) {
      return state // No change if document not found
    }

    const newArray = [...state.signableDocuments]
    newArray[index] = { ...payload.payload.data }
    return {
      ...state,
      signableDocuments: newArray,
    }
  },

  [types.REMOVE_SIGNABLE_DOCUMENTS_FOR_CONTRACT]: (state: any, payload: any) => {
    const { contractId, documentTypes } = payload.payload
    const typeSet = new Set<string>(documentTypes)
    return {
      ...state,
      signableDocuments: state.signableDocuments.filter(
        (doc: SignableDocument) =>
          !(doc.contractId === contractId && typeSet.has(doc.documentType)),
      ),
    }
  },

  [types.GET_FM_METADATAS]: (state: any, payload: any) => {
    return {
      ...state,
      lockedFMMetadatas: payload.payload,
    }
  },

  [types.ADD_LOCKED_FM_METADATA]: (state: any, payload: any) => {
    return {
      ...state,
      lockedFMMetadatas: [...state.lockedFMMetadatas, payload.payload],
    }
  },

  [types.REMOVE_LOCKED_FM_METADATA]: (state: any, payload: any) => {
    return {
      ...state,
      lockedFMMetadatas: state.lockedFMMetadatas.filter(
        (fm: LockedFMMetadata) => fm.signableDocumentId._2 !== payload.payload,
      ),
    }
  },

  [types.SET_FM_METADATA]: (state: any, payload: any) => {
    const { subTradeId, data } = payload.payload
    return {
      ...state,
      fmMetadataBySubTrade: {
        ...state.fmMetadataBySubTrade,
        [subTradeId]: { data, isLoading: false, error: null },
      },
    }
  },

  [types.SET_FM_METADATA_LOADING]: (state: any, payload: any) => {
    const { subTradeId, isLoading } = payload.payload
    return {
      ...state,
      fmMetadataBySubTrade: {
        ...state.fmMetadataBySubTrade,
        [subTradeId]: {
          ...(state.fmMetadataBySubTrade[subTradeId] ?? { data: null, error: null }),
          isLoading,
        },
      },
    }
  },

  [types.SET_FM_METADATA_ERROR]: (state: any, payload: any) => {
    const { subTradeId, error } = payload.payload
    return {
      ...state,
      fmMetadataBySubTrade: {
        ...state.fmMetadataBySubTrade,
        [subTradeId]: { data: null, isLoading: false, error },
      },
    }
  },

  [types.SET_SIGNABLE_DOCUMENTS_GENERATING]: (state: any, payload: any) => {
    return {
      ...state,
      isGenerating: payload.payload,
    }
  },
})

export default documentsReducer
